import { buildSortQueryParams, parseSortSearchParams } from '~/utils/sort-params';

// Must match the API allowlist for GET /contacts.
export const CONTACT_SORT_KEYS = [
  'name',
  'companyName',
  'address1',
  'addressStatus',
  'lastMailedDate',
  'lastMailedStatus',
  'lastQrScan',
  'lastPurlVisitedAt',
  'createdAt',
];

const ARRAY_FILTER_KEYS = ['tags', 'addressStatus', 'lastMailedStatus', 'contactType'];
const DATE_RANGE_FILTER_KEYS = ['lastMailedDate', 'createdAt', 'lastQrScannedDate'];
const BOOLEAN_FILTER_KEYS = ['activeMailing', 'excludeDncList'];
const BOOLEAN_SCOPE_KEYS = ['isReturnAddress'];

const isDateRange = (value) => value && value.startDate && value.endDate;

const getOptionValue = (value) => {
  if (value && typeof value === 'object') {
    return value.title || value.value || value.id;
  }

  return value;
};

export const countContactFilters = (filters = {}) => {
  return Object.keys(filters).filter((key) => {
    const value = filters[key];

    if (BOOLEAN_FILTER_KEYS.includes(key)) {
      return value === true;
    }

    if (BOOLEAN_SCOPE_KEYS.includes(key)) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (DATE_RANGE_FILTER_KEYS.includes(key)) {
      return isDateRange(value);
    }

    return value !== null && value !== undefined && String(value).trim() !== '';
  }).length;
};

export const parseContactQueryParams = (searchParams) => {
  const filters = {};

  ARRAY_FILTER_KEYS.forEach((key) => {
    const values = searchParams.getAll(key).filter((value) => value !== '');

    if (values.length > 0) {
      filters[key] = key === 'tags' ? values.map(Number).filter(Number.isFinite) : values;
    } else {
      filters[key] = [];
    }
  });

  DATE_RANGE_FILTER_KEYS.forEach((key) => {
    const startDate = searchParams.get(`${key}Start`);
    const endDate = searchParams.get(`${key}End`);

    filters[key] = startDate && endDate ? { startDate, endDate } : null;
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    filters[key] = searchParams.get(key) === 'true';
  });

  BOOLEAN_SCOPE_KEYS.forEach((key) => {
    if (searchParams.has(key)) {
      filters[key] = searchParams.get(key) === 'true';
    }
  });

  const propertyAddress = searchParams.get('propertyAddress');

  if (propertyAddress && propertyAddress.trim()) {
    filters.propertyAddress = propertyAddress.trim();
  }

  const filterSize = countContactFilters(filters);

  return {
    search: searchParams.get('search') || '',
    applyFilters: filterSize > 0,
    filterSize,
    filters,
    sort: parseSortSearchParams(searchParams, CONTACT_SORT_KEYS),
  };
};

export const buildContactQueryParams = ({ search, filters = {}, sort }) => {
  const params = {};

  if (search && search.trim()) {
    params.search = search.trim();
  }

  ARRAY_FILTER_KEYS.forEach((key) => {
    if (Array.isArray(filters[key]) && filters[key].length > 0) {
      params[key] = filters[key]
        .map(getOptionValue)
        .filter((value) => value !== null && value !== undefined)
        .map(String);
    }
  });

  DATE_RANGE_FILTER_KEYS.forEach((key) => {
    if (isDateRange(filters[key])) {
      params[`${key}Start`] = filters[key].startDate;
      params[`${key}End`] = filters[key].endDate;
    }
  });

  BOOLEAN_FILTER_KEYS.forEach((key) => {
    if (filters[key] === true) {
      params[key] = 'true';
    }
  });

  BOOLEAN_SCOPE_KEYS.forEach((key) => {
    if (typeof filters[key] === 'boolean') {
      params[key] = String(filters[key]);
    }
  });

  if (filters.propertyAddress && String(filters.propertyAddress).trim()) {
    params.propertyAddress = String(filters.propertyAddress).trim();
  }

  return { ...params, ...buildSortQueryParams(sort) };
};
